import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/containers/contexts/auth-provider';
import { useUserStore } from 'src/store/user-store';
import { useAvailableSites } from './api/use-available-sites';
import { useAvailableTenants } from './api/use-available-tenants';

type ReturnType = { tenantContext: boolean; siteContext: boolean };

export const useSetViewContext = (): ReturnType => {
  const viewContext = useUserStore(state => state.viewContext);
  const setViewContext = useUserStore(state => state.setViewContext);
  const setIsLoadingViewContextAccess = useUserStore(
    state => state.setIsLoadingViewContextAccess,
  );
  const clearDirectoryToken = useUserStore(state => state.clearDirectoryToken);
  const isChangingDirectoryToken = useUserStore(
    state => state.isChangingDirectoryToken,
  );

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [access, setAccess] = useState({
    tenantContext: false,
    siteContext: false,
  });

  const [hasFetchedAccess, setHasFetchedAccess] = useState(false);

  const availableTenants = useAvailableTenants(!!auth.session?.access_token);

  useEffect(() => {
    if (
      availableTenants.isSuccess &&
      availableTenants.data?.resources.length > 0
    ) {
      setAccess(prev => ({
        ...prev,
        tenantContext: true,
      }));
    }

    if (
      availableTenants.isError ||
      (!availableTenants.isPending &&
        availableTenants.data?.resources.length === 0)
    ) {
      setAccess(prev => ({
        ...prev,
        tenantContext: false,
      }));
    }
  }, [
    availableTenants.isSuccess,
    availableTenants.isError,
    availableTenants.data?.resources.length,
    availableTenants.isPending,
  ]);

  const availableSites = useAvailableSites(!!auth.session?.access_token);

  useEffect(() => {
    if (availableSites.isSuccess && availableSites.data?.resources.length > 0) {
      setAccess(prev => ({
        ...prev,
        siteContext: true,
      }));
    }

    if (
      availableSites.isError ||
      (!availableSites.isPending && availableSites.data?.resources.length === 0)
    ) {
      setAccess(prev => ({
        ...prev,
        siteContext: false,
      }));
    }
  }, [
    availableSites.isSuccess,
    availableSites.isError,
    availableSites.data?.resources.length,
    availableSites.isPending,
  ]);

  useEffect(() => {
    if (
      (availableTenants.isSuccess || availableTenants.isError) &&
      (availableSites.isSuccess || availableSites.isError)
    ) {
      setHasFetchedAccess(true);
    }
  }, [
    availableSites.isError,
    availableSites.isSuccess,
    availableTenants.isError,
    availableTenants.isSuccess,
  ]);

  const memoizedPathname = useMemo(
    () => location.pathname,
    [location.pathname],
  );

  useEffect(() => {
    if (hasFetchedAccess && !isChangingDirectoryToken) {
      const pathnameStartsWithTenant = memoizedPathname.startsWith('/tenant');
      const pathnameStartsWithSite = memoizedPathname.startsWith('/site');

      switch (true) {
        // No access to any context do nothing first, test it after release
        case !access.siteContext && !access.tenantContext:
          setIsLoadingViewContextAccess(false);
          break;

        // If pathname starts with "/tenant/" and tenant context is available
        case pathnameStartsWithTenant &&
          access.tenantContext &&
          viewContext !== 'tenant':
          clearDirectoryToken();
          setViewContext('tenant');
          setIsLoadingViewContextAccess(false);

          navigate({
            to: '/',
          });

          break;

        // If pathname starts with "/tenant/" and tenant context is not available
        case pathnameStartsWithTenant && !access.tenantContext:
          setIsLoadingViewContextAccess(false);

          navigate({
            to: '/site',
          });

          break;

        // If pathname starts with "/site/" and site context is available
        case pathnameStartsWithSite &&
          access.siteContext &&
          viewContext !== 'site':
          clearDirectoryToken();
          setViewContext('site');
          setIsLoadingViewContextAccess(false);

          navigate({
            to: '/',
          });

          break;

        // If pathname starts with "/site/" and site context is not available
        case pathnameStartsWithSite && !access.siteContext:
          setIsLoadingViewContextAccess(false);

          navigate({
            to: '/tenant',
          });

          break;

        case access.siteContext && access.tenantContext && viewContext === null:
          setViewContext('tenant');
          setIsLoadingViewContextAccess(false);

          navigate({
            to: '/',
          });

          break;

        default:
          setIsLoadingViewContextAccess(false);
          break;
      }
    }
  }, [
    access.siteContext,
    access.tenantContext,
    navigate,
    setIsLoadingViewContextAccess,
    setViewContext,
    viewContext,
    hasFetchedAccess,
    memoizedPathname,
    isChangingDirectoryToken,
    clearDirectoryToken,
  ]);

  return access;
};
